import type { ReactNode } from 'react';

import type { Adoption, Intent } from '../api/types';
import { BarChart } from '../charts/charts';
import { Async, Grid, PanelCard, PanelError } from '../components/Panel';
import { Muted, Table } from '../components/Table';
import { TileRow, type TileSpec } from '../components/Tiles';
import { useApi } from '../hooks/useApi';
import { intentLabel, num, pct, plural, roleLabel } from '../lib/format';
import { useApp } from '../state/app';

/**
 * Use, in the terms this page is about: people rather than messages, and whether they came
 * back. A tool nobody returns to was tried, not adopted, and the raw question count cannot
 * tell those apart.
 */
function tilesFor(data: Adoption, word: (full: string, plain: string) => string): TileSpec[] {
  return [
    {
      label: word('Identified people', 'People we can name'),
      value: num(data.known_users),
      hint: 'questions asked without a login cannot be counted as people',
    },
    {
      label: word('Returning', 'Came back later'),
      value: num(data.returning_users),
      hint: data.known_users
        ? `of ${plural(data.known_users, 'person', 'people')} — asked again in a later week`
        : 'nobody signed in has been identified yet',
    },
    {
      label: word('Turns per conversation', 'Questions per conversation'),
      value: num(data.avg_turns_per_conversation, 1),
      hint: 'how much people ask once they have started',
    },
  ];
}

/** Below this many questions, a rate is written as the fraction it was worked out from,
 *  since "100%" over fourteen and over four hundred are not the same claim. */
const RATE_SAMPLE = 20;

function escalated(row: Intent): ReactNode {
  if (row.escalation_rate == null) return <Muted>—</Muted>;
  if (row.turns >= RATE_SAMPLE) return pct(row.escalation_rate);
  return (
    <Muted>{`${num(Math.round(row.escalation_rate * row.turns))} of ${num(row.turns)}`}</Muted>
  );
}

export function AdoptionPanel() {
  const { days, system, simple, word } = useApp();
  const adoption = useApi<Adoption>('/api/adoption', { days, system });
  const intents = useApi<{ items: Intent[] }>('/api/intents', { days, system });

  const error = adoption.error ?? intents.error;
  if (error) return <PanelError error={error} />;

  return (
    <Async query={adoption} shape="page">
      {(data) => (
        <div className="space-y-5">
          <TileRow tiles={tilesFor(data, word)} />

          <Grid>
            <PanelCard
              title="By role"
              simpleTitle="By kind of user"
              hint="A role with almost no traffic is either untrained or badly served."
              simpleHint="A group that barely appears here does not know it exists, or was not helped."
            >
              <BarChart
                label="Questions asked, by role"
                rows={data.by_role.map((r) => ({
                  label: roleLabel(r.user_role),
                  value: r.turns,
                  detail: [
                    { label: 'Questions', value: num(r.turns) },
                    { label: 'People', value: num(r.users) },
                    ...(simple ? [] : [{ label: 'Avg evidence', value: num(r.avg_confidence, 2) }]),
                  ],
                }))}
              />
            </PanelCard>

            <PanelCard
              title="By system"
              hint="Where the assistant is embedded, and where it is actually used."
            >
              <Table
                rows={data.by_system}
                columns={[
                  { key: 'system', label: 'System' },
                  { key: 'turns', label: 'Turns', simple: 'Questions', num: true },
                  { key: 'conversations', label: 'Conversations', num: true },
                  { key: 'users', label: 'People', num: true },
                  {
                    id: 'avg_confidence',
                    label: 'Avg evidence',
                    num: true,
                    advanced: true,
                    render: (r) => num(r.avg_confidence, 2),
                    sortValue: (r) => r.avg_confidence,
                  },
                ]}
              />
            </PanelCard>

            {/* Across both columns: it is the odd card of three, and a half-width card with
                an empty half beside it reads as one that failed to load. It is also the
                widest table on the panel, so the room is not spent on nothing. */}
            <PanelCard
              className="xl:col-span-2"
              title="What people come for"
              simpleTitle="What people ask about"
              hint="Intent mix, and how well each kind is served."
              simpleHint="The kinds of question people bring, and how often each needs a human."
            >
              <Async query={intents}>
                {(data) => (
                  <Table
                    rows={data.items}
                    columns={[
                      {
                        id: 'intent',
                        label: 'Intent',
                        simple: 'Kind of question',
                        render: (r) => intentLabel(r.intent),
                        sortValue: (r) => intentLabel(r.intent),
                      },
                      { key: 'turns', label: 'Questions', num: true },
                      {
                        id: 'avg_confidence',
                        label: 'Avg evidence',
                        num: true,
                        advanced: true,
                        render: (r) => num(r.avg_confidence, 2),
                        sortValue: (r) => r.avg_confidence,
                      },
                      {
                        id: 'escalation_rate',
                        label: 'Escalated',
                        simple: 'Ended with a person',
                        num: true,
                        render: escalated,
                        sortValue: (r) => r.escalation_rate,
                      },
                    ]}
                  />
                )}
              </Async>
            </PanelCard>
          </Grid>
        </div>
      )}
    </Async>
  );
}
